import React, { useState } from "react";
import { Link } from "gatsby";
import styled from "styled-components";
import Spinner from "react-spinkit";
import { useIntl } from "gatsby-plugin-intl";

import Tooltip from "./tooltip";

const StyledInput = styled.input`
  display: ${(props) => (props.isSuccess ? "none" : "block")};
`;

const StyledFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  position: relative;
`;

const StyledButton = styled.div`
  transition: background-color 0.5s;
  pointer-events: ${(props) =>
    props.isLoading || props.isSuccess ? "none" : "auto"};
  background-color: ${(props) =>
    props.isSuccess && props.theme.color.dimGrey} !important;
`;

const StyledButtonText = styled.div`
  display: ${(props) => (props.isLoading ? "none" : "block")};
`;

const WrappedSpinner = ({ isLoading, ...props }) => {
  return <Spinner {...props} />;
};

const StyledSpinner = styled(WrappedSpinner)`
  display: ${(props) => (props.isLoading ? "block" : "none")};
`;

const StyledModalSignupForm = styled.div`
  margin-top: 1.6rem;
  ${(props) => props.theme.responsive.desktop} {
    margin-top: 2.2rem;
  }
  width: 100%;
  ${StyledInput} {
    width: 100%;
    background-color: transparent;
    outline: none;
    border: none;
    border-radius: 0;
    border-bottom: 2px solid ${(props) => props.theme.color.white};
    color: white;
    font-size: 0.6rem;
    padding: 3px 0;
    font-family: ${(props) => props.theme.font.sans};
    font-size: 1.4rem;

    &::placeholder {
      opacity: 0.8;
      color: ${(props) => props.theme.color.white};
    }

    &:focus {
      border-color: white;
    }
  }

  ${StyledButton} {
    font-family: ${(props) => props.theme.font.sans};
    font-weight: bold;
    color: white;
    background-color: ${(props) =>
      props.isSuccess ? props.theme.color.black : props.theme.color.blue};
    border-radius: 2px;
    font-size: 0.8rem;
    min-width: 9rem;
    padding: 0 0.6rem;
    min-height: 2.6rem;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      cursor: pointer;
      background-color: ${(props) => props.theme.color.darkGrey};
    }
  }
`;

const StyledButtonWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledPrivacyPolicy = styled.div`
  font-size: 0.6rem;
  line-height: 0.8rem;
  color: white;
  margin-left: 1rem;
  font-family: ${(props) => props.theme.font.sans};
`;

const StyledLink = styled.span`
  color: ${(props) => props.theme.color.blue};

  &:hover {
    cursor: pointer;
  }
`;

const EmailSignUpForm = () => {
  const intl = useIntl();
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email) => {
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleEmailSignUp = () => {
    if (!inputText || !validateEmail(inputText)) {
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);
    const headers = new Headers();
    headers.append(
      "Authorization",
      "Bearer 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"
    );
    headers.append("Content-Type", "application/json");
    const body = {
      list_ids: ["2af4e6ae-6b52-4b5b-8e46-425d9dc5775d"],
      contacts: [
        {
          email: inputText,
        },
      ],
    };
    var request = new Request(
      "https://api.sendgrid.com/v3/marketing/contacts",
      {
        method: "PUT",
        headers: headers,
        body: JSON.stringify(body),
      }
    );
    fetch(request)
      .then((response) => response.json())
      .then((data) => {
        setIsLoading(false);
        setIsError(false);
        setIsSuccess(true);
        setInputText("");
      })
      .catch((e) => {
        setIsLoading(false);
        setIsError(true);
        setIsSuccess(false);
      });
  };

  const resetState = () => {
    setIsLoading(false);
    setIsError(false);
    setIsSuccess(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === `Enter`) {
      handleEmailSignUp();
    }
  };

  return (
    <>
      <StyledFormWrapper>
        <StyledInput
          isSuccess={isSuccess}
          placeholder={intl.formatMessage({
            id: `botbar.email.placeholder`,
          })}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <StyledButtonWrapper>
          <StyledButton
            isSuccess={isSuccess}
            isLoading={isLoading}
            onClick={() => handleEmailSignUp()}
          >
            <StyledButtonText isSuccess={isSuccess} isLoading={isLoading}>
              {isSuccess
                ? intl.formatMessage({
                    id: `botbar.email.buttonSuccess`,
                  })
                : intl.formatMessage({
                    id: `botbar.email.button`,
                  })}
            </StyledButtonText>
            <StyledSpinner
              fadeIn="none"
              isLoading={isLoading}
              color="white"
              name="circle"
            />
          </StyledButton>
          <StyledPrivacyPolicy>
            To opt out, click Unsubscribe at the bottom of our emails. By
            signing up you agree with our{" "}
            <Link to="/terms-of-service">
              <StyledLink>Terms & Conditions</StyledLink>
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy">
              <StyledLink>Privacy Policy</StyledLink>
            </Link>
            .
          </StyledPrivacyPolicy>
        </StyledButtonWrapper>
        <Tooltip
          isShown={isError}
          handleClick={() => resetState()}
          tip={intl.formatMessage({ id: `botbar.email.error` })}
        />
      </StyledFormWrapper>
    </>
  );
};

export {
  StyledModalSignupForm,
  StyledButtonWrapper,
  StyledButton,
  StyledInput,
  EmailSignUpForm,
};
